import { useState, useEffect, useCallback, useRef } from "react";

interface SlideData {
  id: number;
  image: string;
  titleKey: string;
  subtitleKey?: string;
  titleAnimations: string[];
  subtitleAnimations?: string[];
  slideDuration: number;
}

interface HeroSliderTexts {
  slide1_title: string;
  slide2_title: string;
  slide3_title: string;
  slide4_title: string;
  slide4_subtitle: string;
  slide5_title: string;
  prev_slide: string;
  next_slide: string;
  slide_label: string;
  alts: string[];
}

const slideAlts: string[] = [
  "Finger berührt einen digital vernetzten Globus für risikoarmen ITK-Markteintritt.",
  "Finger markiert Europa auf einer vernetzten Weltkarte für den ITK-Markteintritt.",
  "Blaue Deutschlandkarte mit Bundesländern symbolisiert lokale ITK-Partnerschaft beim Markteintritt.",
  "Geschäftsmann bedient digitale Oberfläche für ITK-Services beim DACH-Markteintritt.",
  "Finger aktiviert digitales Startsymbol für erfolgreichen ITK-Markteintritt in Europa.",
];

const slidesData: SlideData[] = [
  {
    id: 1,
    image: "https://net-basis.net/Bilder/global-ict-market-entry-risk-reduction.webp",
    titleKey: "hero.slide1_title",
    titleAnimations: ["hero-loop-top-bottom"],
    slideDuration: 8000,
  },
  {
    id: 2,
    image: "https://net-basis.net/Bilder/ict-market-entry-europe-global-networking.webp",
    titleKey: "hero.slide2_title",
    titleAnimations: ["hero-loop-top-bottom"],
    slideDuration: 8000,
  },
  {
    id: 3,
    image: "https://net-basis.net/Bilder/germany-dach-market-entry-local-partner.webp",
    titleKey: "hero.slide3_title",
    titleAnimations: ["hero-loop-right-left"],
    slideDuration: 8000,
  },
  {
    id: 4,
    image: "https://net-basis.net/Bilder/dach-market-entry-ict-business-services.webp",
    titleKey: "hero.slide4_title",
    subtitleKey: "hero.slide4_subtitle",
    titleAnimations: ["hero-loop-s4-title"],
    subtitleAnimations: [
      "hero-loop-s4-sub1",
      "hero-loop-s4-sub2",
      "hero-loop-s4-sub3",
      "hero-loop-s4-sub4",
    ],
    slideDuration: 15000,
  },
  {
    id: 5,
    image: "https://net-basis.net/Bilder/ict-market-entry-europe-digital-start.webp",
    titleKey: "hero.slide5_title",
    titleAnimations: ["hero-loop-top-bottom"],
    slideDuration: 8000,
  },
];

function computeDirection(from: number, to: number, total: number): number {
  if (to > from) {
    return (to - from <= total / 2) ? 1 : -1;
  }
  return (from - to <= total / 2) ? -1 : 1;
}

interface HeroSliderProps {
  texts?: HeroSliderTexts;
}

export default function HeroSlider({ texts }: HeroSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState(1);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const [transitionActive, setTransitionActive] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [showText, setShowText] = useState(false);
  const [imageHeights, setImageHeights] = useState<Record<number, number>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1100);

  const isTransitioningRef = useRef(false);
  const currentSlideRef = useRef(0);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  // Build slides from texts prop (always provided by all 4 language Home pages)
  const resolvedSlides = slidesData.map((slide, idx) => ({
    ...slide,
    title: texts
      ? idx === 0 ? texts.slide1_title
      : idx === 1 ? texts.slide2_title
      : idx === 2 ? texts.slide3_title
      : idx === 3 ? texts.slide4_title
      : texts.slide5_title
      : "",
    subtitle: slide.subtitleKey && texts ? texts.slide4_subtitle : undefined,
  }));

  const resolvedTitleTexts = texts
    ? [texts.slide1_title, texts.slide2_title, texts.slide3_title, texts.slide4_title, texts.slide5_title]
    : null;

  const resolvedAlts = texts?.alts ?? [];

  const prevLabel = texts?.prev_slide ?? "";
  const nextLabel = texts?.next_slide ?? "";
  const slideLabel = texts?.slide_label ?? "";

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    slidesData.forEach((slide) => {
      const img = new Image();
      img.onload = () => {
        setImageHeights((prev) => ({
          ...prev,
          [slide.id]: img.naturalHeight / img.naturalWidth,
        }));
      };
      img.src = slide.image;
    });
  }, []);

  const clearAllTimers = useCallback(() => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (textTimerRef.current) {
      clearTimeout(textTimerRef.current);
      textTimerRef.current = null;
    }
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      const transitioning = isTransitioningRef.current;
      const current = currentSlideRef.current;

      if (transitioning || index === current) return;

      clearAllTimers();

      const dir = computeDirection(current, index, slidesData.length);
      setDirection(dir);
      setPrevSlide(current);
      setCurrentSlide(index);
      setIsTransitioning(true);
      setTransitionActive(false);
      setShowText(false);
      setAnimationKey((prev) => prev + 1);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionActive(true);
        });
      });

      transitionTimerRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setPrevSlide(null);
        setTransitionActive(false);
        setShowText(true);
      }, 3500);
    },
    [clearAllTimers],
  );

  const nextSlide = useCallback(() => {
    const current = currentSlideRef.current;
    goToSlide((current + 1) % slidesData.length);
  }, [goToSlide]);

  const prevSlideFn = useCallback(() => {
    const current = currentSlideRef.current;
    goToSlide((current - 1 + slidesData.length) % slidesData.length);
  }, [goToSlide]);

  useEffect(() => {
    if (isTransitioning) return;

    const duration = slidesData[currentSlide].slideDuration;

    autoplayTimerRef.current = setTimeout(() => {
      nextSlide();
    }, duration);

    return () => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }
    };
  }, [currentSlide, isTransitioning, nextSlide]);

  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  const slide = resolvedSlides[currentSlide];
  const prevSlideData = prevSlide !== null ? resolvedSlides[prevSlide] : null;

  const currentRatio = imageHeights[slide.id];

  const getMinHeight = (): number => {
    const w = containerWidth;
    if (w >= 1024) return 520;
    if (w >= 768) return 420;
    return 280;
  };

  const containerHeight = currentRatio
    ? Math.max(Math.round(containerWidth * currentRatio), getMinHeight())
    : Math.max(Math.round(containerWidth * 0.66), getMinHeight());

  const renderTextContent = (s: (typeof resolvedSlides)[number]) => {
    const tl = s.title.split("\n");
    const sl = s.subtitle ? s.subtitle.split("\n") : [];
    const isRightAligned = s.id === 3 || s.id === 4;

    const paddingTopClass =
      s.id === 3
        ? "pt-12 md:pt-14 lg:pt-20"
        : s.id === 4
          ? "pt-7 md:pt-10 lg:pt-16"
          : s.id === 5
            ? "pt-10 md:pt-16 lg:pt-20"
            : "pt-6 md:pt-16 lg:pt-20";

    return (
      <div
        className={`absolute inset-0 z-20 flex flex-col ${isRightAligned ? "items-end" : "items-center"} justify-start ${paddingTopClass} px-6 md:px-10 pointer-events-none`}
      >
        <div
          className={`${isRightAligned ? "text-right" : "text-center"} ${isRightAligned ? "md:max-w-[480px] lg:max-w-[540px]" : "max-w-4xl"}`}
        >
          <div
            className={`flex flex-col ${isRightAligned ? "items-end" : "items-center"} gap-1 md:gap-2`}
          >
            {tl.map((line, i) => (
              <p
                key={`title-${i}`}
                className={`text-3xl md:text-4xl lg:text-5xl font-normal text-white leading-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] ${
                  s.titleAnimations[i] || "hero-loop-top-bottom"
                }`}
                style={{
                  fontFamily: '"Open Sans", sans-serif',
                }}
              >
                {line}
              </p>
            ))}
          </div>

          {sl.length > 0 && (
            <div
              className={`mt-4 md:mt-6 flex flex-col ${isRightAligned ? "items-end" : "items-center"} gap-1`}
            >
              {sl.map((line, i) => (
                <p
                  key={`sub-${i}`}
                  className={`text-base md:text-xl lg:text-2xl font-semibold leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] ${
                    s.subtitleAnimations?.[i] ||
                    "hero-loop-s4-sub1"
                  } text-white`}
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const outgoingTransform =
    isTransitioning && transitionActive
      ? `translateX(${direction * -100}%)`
      : "translateX(0)";
  const outgoingTransition = transitionActive
    ? "transform 3s ease-in-out, opacity 3s ease-in-out"
    : "none";

  const incomingTransform = isTransitioning
    ? transitionActive
      ? "translateX(0)"
      : `translateX(${direction * 100}%)`
    : "translateX(0)";
  const incomingTransition =
    isTransitioning && transitionActive
      ? "transform 3s ease-in-out, opacity 3s ease-in-out"
      : "none";

  return (
    <section className="w-full bg-white py-4 md:py-6 lg:py-8">
      <div ref={containerRef} className="w-full max-w-[1100px] mx-auto">
        <div
          className="relative w-full overflow-hidden transition-[height] duration-500"
          style={{ height: `${containerHeight}px` }}
          role="region"
          aria-roledescription="carousel"
          aria-label={texts?.slide_label || "Slider"}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") { e.preventDefault(); prevSlideFn(); }
            if (e.key === "ArrowRight") { e.preventDefault(); nextSlide(); }
          }}
        >
          {isTransitioning && prevSlideData && (
            <div
              className="absolute inset-0 z-10"
              style={{
                transform: outgoingTransform,
                opacity: isTransitioning && transitionActive ? 0 : 1,
                transition: outgoingTransition,
              }}
            >
              <img loading="lazy"
                src={prevSlideData.image}
                alt={resolvedAlts[prevSlide ?? 0] || resolvedAlts[0]}
                title={resolvedTitleTexts?.[prevSlide ?? 0] || undefined}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50 pointer-events-none" />
            </div>
          )}

          <div
            className="absolute inset-0 z-20"
            style={{
              transform: incomingTransform,
              opacity: isTransitioning ? (transitionActive ? 1 : 0) : 1,
              transition: incomingTransition,
            }}
          >
            <img loading="lazy"
              src={slide.image}
              alt={resolvedAlts[currentSlide] || resolvedAlts[0]}
              title={resolvedTitleTexts?.[currentSlide] || undefined}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50 pointer-events-none" />
            {showText && (
              <div key={`text-${animationKey}-${slide.id}`}>
                {renderTextContent(slide)}
              </div>
            )}
          </div>

          <button
            onClick={prevSlideFn}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/25 hover:bg-white/45 backdrop-blur-sm transition-colors cursor-pointer"
            aria-label={prevLabel}
          >
            <i className="ri-arrow-left-s-line text-white text-xl md:text-2xl" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/25 hover:bg-white/45 backdrop-blur-sm transition-colors cursor-pointer"
            aria-label={nextLabel}
          >
            <i className="ri-arrow-right-s-line text-white text-xl md:text-2xl" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2 md:gap-3">
            {resolvedSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="group w-6 h-6 md:w-7 md:h-7 flex items-center justify-center cursor-pointer"
                aria-label={slideLabel.replace("{{number}}", String(index + 1))}
                aria-current={index === currentSlide ? "true" : "false"}
              >
                <span
                  className={`block w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "bg-primary-400 scale-110"
                      : "bg-white/60 group-hover:bg-white/80"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}